/** @jsxImportSource @emotion/react */
import { Box, Theme, useTheme } from '@mui/material';
import Heading from 'components/Heading';
import CommonPageLayout from 'components/layouts/CommonPageLayout';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { UseStyles } from 'types/styles';
import FileUploadOutlinedIcon from '@mui/icons-material/FileUploadOutlined';
import { UploadForm } from 'components/forms/UploadForm/UploadForm';
import { useNavigate } from 'react-router-dom';
import { useMessages } from 'contexts/message';
import { usePaths } from 'hooks/usePaths';
import { Helmet } from 'react-helmet';
import { useCheckAuth } from 'hooks/useCheckAuth';

const UploadFormPage: React.FC = () => {
  useCheckAuth();
  const theme = useTheme() as Theme;
  const classes = useStyles(theme);
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const { add } = useMessages();
  const paths = usePaths();

  const scrollToTop = () => window.scrollTo({ top: 0, behavior: 'smooth' });

  return (
    <CommonPageLayout>
      <Helmet>
        <title>{formatMessage({ id: 'pageTitle.uploadForm', defaultMessage: 'Upload Form' })}</title>
      </Helmet>
      <Box p={8}>
        <Heading mb={5} css={classes.heading}>
          <FileUploadOutlinedIcon css={classes.headingIcon} />
          <FormattedMessage id={'UploadFormPage.uploadNewVersion'} defaultMessage={'Upload new version'} />
        </Heading>
        <UploadForm
          onSuccessSubmit={({ template, country, adminArea }) => {
            add({
              severity: 'success',
              message: formatMessage(
                {
                  id: 'UploadForm.succeededSubmit',
                  defaultMessage: '{template} plan & budget for {country}/{adminArea} uploaded successfully.',
                },
                {
                  template: template?.name,
                  country: country?.name || 'Unknown',
                  adminArea: adminArea?.name || formatMessage({ id: 'common.national', defaultMessage: 'National' }),
                },
              ),
            });
            navigate(paths.uploads.generate());
            scrollToTop();
          }}
          onFailSubmit={scrollToTop}
        />
      </Box>
    </CommonPageLayout>
  );
};

const useStyles = (theme: Theme): UseStyles => ({
  heading: {
    display: 'flex',
    alignItems: 'center',
  },
  headingIcon: {
    fontSize: 32,
    marginInlineEnd: theme.spacing(2),
    color: theme.palette.primary.main,
    flexShrink: 0,
  },
});

export default UploadFormPage;
